<app-public-mit-shell [logoUrl]="mandate()?.merchantLogoUrl" [merchantName]="mandate()?.merchantName">
  @if (loading()) {
    <div class="flex-1 flex items-center justify-center min-h-[50vh]">
      <div class="inline-block animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600"></div>
    </div>
  } @else if (invalid()) {
    <div class="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 text-center">
      <i class="pi pi-exclamation-triangle text-3xl text-red-400"></i>
      <p class="text-slate-800 font-semibold mt-3 mb-0">Liga inválida o vencida</p>
    </div>
  } @else if (mandate(); as m) {
    <section class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div class="bg-gradient-to-br from-slate-800 to-slate-900 px-6 py-5 flex items-start justify-between gap-3">
        <div>
          <p class="text-slate-300 text-xs uppercase tracking-widest m-0">
            Autorización de cobros@if (m.number) { · <span class="font-mono normal-case tracking-normal text-white">{{ m.number }}</span> }
          </p>
          <h1 class="text-white text-xl font-bold leading-tight mt-1 mb-0">{{ m.merchantName }}</h1>
          <p class="text-slate-300 text-sm mt-1 mb-0">{{ m.purpose }}</p>
        </div>
        <span class="shrink-0 rounded-full px-3 py-1 text-xs font-semibold" [class]="statusClasses()">{{ statusLabel() }}</span>
      </div>
      <div class="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div class="sm:col-span-2">
          <p class="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Vigencia</p>
          <p class="m-0 text-slate-800">
            {{ m.validFrom ? (m.validFrom | date:'dd/MM/yyyy') : 'Desde su registro' }} — {{ m.validUntil ? (m.validUntil | date:'dd/MM/yyyy') : 'hasta que la revoques' }}
          </p>
        </div>
        <div class="sm:col-span-2">
          <p class="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Límites</p>
          <ul class="m-0 pl-4 list-disc text-slate-700">
            @for (row of limitRows(); track row.label) {
              <li>{{ row.label }}: <strong>{{ row.value }}</strong></li>
            }
          </ul>
        </div>
      </div>
    </section>

    <section class="bg-white rounded-2xl shadow-sm border border-slate-200">
      <div class="px-6 py-4 border-b border-slate-100">
        <h2 class="text-base font-bold text-slate-800 m-0">Método de pago</h2>
        <p class="text-xs text-slate-500 mt-1 mb-0">Tarjeta con la que {{ m.merchantName }} realiza los cobros de esta autorización.</p>
      </div>
      <div class="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        @if (m.currentCardLastDigits) {
          <div class="flex items-center gap-4 min-w-0">
            <div class="flex h-12 w-[4.5rem] shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 px-1 text-center text-[10px] font-bold uppercase leading-tight tracking-wider text-white shadow-sm">
              {{ cardBrandLabel() || 'Tarjeta' }}
            </div>
            <div class="min-w-0">
              <p class="m-0 text-sm font-semibold text-slate-900">
                {{ cardBrandLabel() || 'Tarjeta' }} <span class="font-mono">•••• {{ m.currentCardLastDigits }}</span>
              </p>
              @if (cardDetails()) {
                <p class="m-0 mt-0.5 text-xs text-slate-500">{{ cardDetails() }}</p>
              }
            </div>
          </div>
        } @else {
          <p class="m-0 text-sm text-slate-500">Sin tarjeta registrada.</p>
        }
        @if (canChangeCard()) {
          <button type="button" (click)="changeCard()" [disabled]="changingCard()"
            class="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            <i [class]="changingCard() ? 'pi pi-spin pi-spinner' : 'pi pi-credit-card'"></i>
            Cambiar método de pago
          </button>
        }
      </div>
      @if (cardsError()) {
        <p class="text-sm text-red-600 px-6 mt-0 mb-3">{{ cardsError() }}</p>
      }
      @if (canChangeCard()) {
        <p class="text-[11px] text-slate-500 px-6 mt-0 mb-4">
          Te llevaremos a capturar la nueva tarjeta y firmar de nuevo la autorización. La actual se seguirá usando hasta que la nueva quede validada.
        </p>
      }
    </section>

    <section class="bg-white rounded-2xl shadow-sm border border-slate-200">
      <div class="px-6 py-4 border-b border-slate-100">
        <h2 class="text-base font-bold text-slate-800 m-0">Tus tarjetas guardadas</h2>
        <p class="text-xs text-slate-500 mt-1 mb-0">Solo tú puedes eliminar tus tarjetas con {{ m.merchantName }}.</p>
      </div>
      <div class="px-6 py-3">
        @if (savedCards().length) {
          <ul class="m-0 p-0 list-none divide-y divide-slate-100 text-sm">
            @for (card of savedCards(); track card.id) {
              <li class="flex items-center justify-between gap-3 py-2">
                <span class="text-slate-800">
                  <span class="font-mono">{{ card.brand || 'Tarjeta' }} •••• {{ card.lastDigits }}</span>
                  <span class="text-slate-500"> · vence {{ card.expirationMonth }}/{{ card.expirationYear }}</span>
                </span>
                @if (card.inUse) {
                  <span class="text-xs text-slate-500">{{ card.currentForMandate ? 'En uso en esta autorización' : 'En uso' }}</span>
                } @else {
                  <button type="button" (click)="openDeleteCard(card)" [disabled]="deletingCardId() != null"
                    class="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                    Eliminar
                  </button>
                }
              </li>
            }
          </ul>
        } @else {
          <p class="text-sm text-slate-500 my-2">No tienes tarjetas guardadas con {{ m.merchantName }}.</p>
        }
      </div>
    </section>

    <section class="bg-white rounded-2xl shadow-sm border border-slate-200">
      <div class="px-6 py-4 border-b border-slate-100">
        <h2 class="text-base font-bold text-slate-800 m-0">Últimos cobros</h2>
      </div>
      <div class="px-6 py-3">
        @if (m.recentCharges?.length) {
          <table class="w-full text-sm">
            <tbody class="divide-y divide-slate-100">
              @for (charge of m.recentCharges; track $index) {
                <tr>
                  <td class="py-2 text-slate-500 whitespace-nowrap">{{ charge.chargedAt | date:'dd/MM/yyyy' }}</td>
                  <td class="py-2 px-3 text-slate-800">{{ charge.concept }}</td>
                  <td class="py-2 text-right font-mono text-slate-900 whitespace-nowrap">{{ charge.amount | currency:(m.currencyCode || 'MXN'):'symbol-narrow' }}</td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <p class="text-sm text-slate-500 my-2">Aún no hay cobros con esta autorización.</p>
        }
      </div>
    </section>

    @if (canRevoke()) {
      <section class="bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p class="text-sm text-slate-600 m-0">Si revocas la autorización, {{ m.merchantName }} ya no podrá cobrar a tu tarjeta.</p>
        <button type="button" (click)="confirmOpen.set(true)"
          class="shrink-0 rounded-xl border border-red-300 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50">
          Revocar autorización
        </button>
      </section>
    } @else if (m.status === 'REVOKED') {
      <div class="rounded-xl border border-slate-200 bg-white px-6 py-4 text-sm text-slate-600">
        Revocaste esta autorización. {{ m.merchantName }} ya no puede hacer cobros con ella.
      </div>
    }

    @if (cardToDelete(); as card) {
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" role="dialog" aria-modal="true"
        aria-labelledby="mitDeleteCardTitle" (click)="closeDeleteCard()">
        <div class="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 flex flex-col gap-4" (click)="$event.stopPropagation()">
          <h3 id="mitDeleteCardTitle" class="text-lg font-bold text-slate-900 m-0">¿Eliminar esta tarjeta?</h3>
          <p class="text-sm text-slate-600 m-0">
            La tarjeta <span class="font-mono font-semibold text-slate-800">{{ card.brand || 'Tarjeta' }} •••• {{ card.lastDigits }}</span>
            ya no quedará guardada con {{ m.merchantName }} y no se podrá usar para sus cobros. Esta acción no se puede deshacer.
          </p>
          @if (deleteCardError()) {
            <p class="text-sm text-red-600 m-0">{{ deleteCardError() }}</p>
          }
          <div class="flex justify-end gap-2">
            <button type="button" (click)="closeDeleteCard()" [disabled]="deletingCardId() != null"
              class="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Cancelar</button>
            <button type="button" (click)="deleteSavedCard()" [disabled]="deletingCardId() != null"
              class="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
              @if (deletingCardId() != null) { <i class="pi pi-spin pi-spinner"></i> } Sí, eliminar
            </button>
          </div>
        </div>
      </div>
    }

    @if (confirmOpen()) {
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" role="dialog" aria-modal="true" aria-labelledby="mitRevokeTitle">
        <div class="w-full max-w-md rounded-2xl bg-white shadow-xl p-6 flex flex-col gap-4">
          <h3 id="mitRevokeTitle" class="text-lg font-bold text-slate-900 m-0">¿Revocar la autorización?</h3>
          <p class="text-sm text-slate-600 m-0">
            {{ m.merchantName }} ya no podrá cobrar a tu tarjeta con esta autorización. Los cobros ya realizados no se cancelan.
          </p>
          @if (error()) {
            <p class="text-sm text-red-600 m-0">{{ error() }}</p>
          }
          <div class="flex justify-end gap-2">
            <button type="button" (click)="confirmOpen.set(false)" [disabled]="revoking()"
              class="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Cancelar</button>
            <button type="button" (click)="revoke()" [disabled]="revoking()"
              class="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
              @if (revoking()) { <i class="pi pi-spin pi-spinner"></i> } Sí, revocar
            </button>
          </div>
        </div>
      </div>
    }
  }
</app-public-mit-shell>
